import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cell"]
  static values = { url: String }

  connect() {
    this.activeInput = null
  }

  edit(event) {
    if (this.activeInput) return
    const cell = event.currentTarget
    const field = cell.dataset.field

    // Sprint 7 (01/06/2026) — Support édition inline pour notes (text) + order_date (date).
    if (field === "notes" || field === "order_date") {
      this.editSimple(cell, field)
      return
    }

    if (field !== "gift_amount_cents") return

    this.originalHTML = cell.innerHTML

    // Sprint 7 (01/06/2026) — Quickfill 50/100 : 90% des cadeaux sont 50€ ou 100€,
    // saisie clavier = friction inutile. Boutons cliquables → 1 click au lieu de 3.
    const wrapper = document.createElement("div")
    wrapper.className = "inline-flex items-center gap-1"

    const input = document.createElement("input")
    input.type = "number"
    input.step = "1"
    input.min = "0"
    input.placeholder = "50 ou 100"
    input.className = "w-20 text-xs px-2 py-1 border border-gold rounded bg-white focus:outline-none text-right"
    const currentCents = cell.dataset.currentValue
    if (currentCents && currentCents !== "") {
      input.value = Math.round(parseInt(currentCents, 10) / 100)
    }

    const btn50 = this.buildQuickBtn("50€", () => this.applyQuickFill(cell, input, 50))
    const btn100 = this.buildQuickBtn("100€", () => this.applyQuickFill(cell, input, 100))

    wrapper.appendChild(input)
    wrapper.appendChild(btn50)
    wrapper.appendChild(btn100)

    cell.innerHTML = ""
    cell.appendChild(wrapper)
    this.activeInput = input
    input.focus()
    input.select()

    input.addEventListener("blur", (e) => {
      // Ignore blur si focus reste sur un des boutons quickfill
      if (e.relatedTarget && wrapper.contains(e.relatedTarget)) return
      this.saveAmount(cell, input)
    })
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); input.blur() }
      if (e.key === "Escape") { this.cancel(cell) }
    })
  }

  buildQuickBtn(label, onClick) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.textContent = label
    btn.className = "text-xs px-2 py-1 border border-gold rounded bg-gold/10 hover:bg-gold/25 text-gold-dark font-semibold focus:outline-none"
    btn.addEventListener("mousedown", (e) => e.preventDefault()) // garde le focus input
    btn.addEventListener("click", onClick)
    return btn
  }

  applyQuickFill(cell, input, euros) {
    input.value = euros
    this.saveAmount(cell, input)
  }

  // Sprint 7 (01/06/2026) — édition inline générique pour notes / order_date.
  editSimple(cell, field) {
    this.originalHTML = cell.innerHTML
    const input = document.createElement("input")
    input.type = field === "order_date" ? "date" : "text"
    input.className = "w-full text-xs px-2 py-1 border border-gold rounded bg-white focus:outline-none"
    input.value = cell.dataset.currentValue || ""

    cell.innerHTML = ""
    cell.appendChild(input)
    this.activeInput = input
    input.focus()
    input.select?.()

    input.addEventListener("blur", () => this.saveSimple(cell, input, field))
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); input.blur() }
      if (e.key === "Escape") { this.cancel(cell) }
    })
  }

  saveSimple(cell, input, field) {
    const raw = input.value.trim()
    const value = raw === "" ? null : raw
    cell.dataset.currentValue = raw
    const display = value || `<span class="text-xs text-text-muted italic">—</span>`
    cell.innerHTML = `<span class="text-sm text-text-secondary">${this.escapeHtml(display)}</span>`
    this.patch({ [field]: value }, cell)
  }

  escapeHtml(str) {
    if (str == null) return ""
    return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }

  toggleDistribution(event) {
    const checked = event.target.checked
    const cell = event.target.closest("td")
    this.patch({ gift_sent_at: checked ? new Date().toISOString() : null }, cell)
  }

  saveAmount(cell, input) {
    const euros = input.value.trim()
    if (euros === "") {
      this.patch({ gift_amount_cents: null }, cell)
      cell.innerHTML = `<span class="text-sm text-text-muted/70 italic">À décider</span>`
    } else {
      const cents = Math.round(parseFloat(euros) * 100)
      cell.dataset.currentValue = cents
      cell.innerHTML = `<span class="text-sm font-semibold text-text-primary">${Math.round(cents / 100)} €</span>`
      this.patch({ gift_amount_cents: cents }, cell)
    }
  }

  cancel(cell) {
    cell.innerHTML = this.originalHTML
    this.activeInput = null
  }

  patch(attrs, cell = null) {
    this.activeInput = null
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (cell) this.flashCell(cell, "saving")

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({ referral: attrs })
    }).then(response => {
      if (response.ok) return
      throw new Error("Save failed")
    }).then(() => {
      if (cell) this.flashCell(cell, "success")
    }).catch(err => {
      console.error("[referral-edit] save failed", err)
      if (cell) this.flashCell(cell, "error")
    })
  }

  flashCell(cell, state) {
    const colors = {
      saving:  "rgba(245, 158, 11, 0.35)",
      success: "rgba(16, 185, 129, 0.35)",
      error:   "rgba(239, 68, 68, 0.40)"
    }
    cell.style.transition = "background-color 0.2s ease-out"
    cell.style.backgroundColor = colors[state] || "transparent"
    if (state === "success" || state === "error") {
      setTimeout(() => { cell.style.backgroundColor = "transparent" }, 700)
    }
  }

  disconnect() {
    this.activeInput = null
  }
}
